<template>
  <div>
    <h4 class="table-page-header">
      Authorizations
      <el-tooltip content="Add New Authorization">
        <el-button type="text" @click="createAuthorization">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search"
            clearable
            @clear="clearSearch"
            @input="onSearch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="payerTypeFilter"
            :options="payerTypeOptions"
            placeholder="Payer Type"
            @change="onPayerTypeFilterChange"
            class="compact-filter"
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="activeStatusFilter"
            :options="activeStatusOptions"
            placeholder="Status"
            @change="onStatusFilterChange"
            class="compact-filter"
          />
        </div>
      </div>
    </div>
    <AIVTable
      :columns="authorizationColumns"
      :data="authorizations"
      :pagination="pagination"
      :item-name="'authorizations'"
      @pageChange="fetchAuthorizations"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
    >
      <template #cell-client_display_name="{ row }">
        <router-link
          v-if="(row.client && row.client.id) || row.id_clients"
          :to="`/pages/clients/${(row.client && row.client.id) || row.id_clients}`"
        >{{ row.client_display_name }}</router-link>
        <template v-else>{{ row.client_display_name }}</template>
      </template>
      <template #cell-actions="{ row }">
        <button
          v-if="!row.auth_number || !row.auth_number.startsWith('D')"
          class="btn btn-sm btn-primary mr-2"
          @click="viewInvoices(row)"
          title="View Invoices"
        >
          <i class="fa-solid fa-file-invoice"></i>
        </button>
        <button
          class="btn btn-sm btn-info mr-2"
          @click="viewApprovedShifts(row)"
          title="View Approved Shifts"
        >
          <i class="fa-solid fa-calendar-check"></i>
        </button>
        <button
          class="btn btn-sm btn-warning mr-2"
          @click="editAuthorization(row)"
          title="Edit"
        >
          <i class="fa-solid fa-pen-to-square"></i>
        </button>
        <button
          v-if="row.status !== 'inactive'"
          class="btn btn-sm btn-danger mr-2"
          @click="
            $confirmAction(
              () => deactivateAuthorization(row),
              `Are you sure you want to deactivate authorization ${row.auth_number}?`,
            )
          "
          title="Deactivate"
        >
          <i class="fa-solid fa-ban"></i>
        </button>
        <button
          v-if="row.status === 'inactive'"
          class="btn btn-sm btn-success mr-2"
          @click="
            $confirmAction(
              () => activateAuthorization(row),
              `Are you sure you want to reactivate authorization ${row.auth_number}?`,
            )
          "
          title="Reactivate"
        >
          <i class="fa-solid fa-check-circle"></i>
        </button>
        <button
          class="btn btn-sm btn-danger"
          @click="
            $confirmAction(
              () => deleteAuthorization(row),
              `Are you sure you want to permanently delete authorization ${row.auth_number}? This action cannot be undone.`,
            )
          "
          title="Delete Permanently"
        >
          <i class="fa-solid fa-trash"></i>
        </button>
      </template>
    </AIVTable>

    <AIVDialog
      v-model="$modal.state.create_or_edit_authorization"
      :title="authorizationModalTitle"
      height="95vh"
      :no-body-padding="true"
      :no-body-overflow="true"
    >
      <!-- Processing uploaded PDF -->
      <div v-if="processing" class="text-center py-5" style="padding: 20px">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Processing...</span>
        </div>
        <p class="mt-2">Processing PDF document...</p>
      </div>

      <!-- Loading existing PDF -->
      <div
        v-else-if="pdfLoading"
        class="text-center py-5"
        style="padding: 20px"
      >
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2">Loading PDF document...</p>
      </div>

      <!-- PDF loaded or no PDF -->
      <div v-else class="pdf-verification-section w-100">
        <!-- Form first, PDF below it — one scroll context (the dialog body),
             so the form is immediately usable and the PDF never squeezes it -->
        <div class="row w-100 m-0">
          <div class="col-12">
            <div class="verification-form">
              <div
                v-if="!authorizationBeingEdited && !pdfUrl"
                class="row mb-3 w-100 mx-0"
              >
                <div class="col-12 col-md-2 px-2 mb-2 mb-md-0">
                  <label class="d-block">Auth Type</label>
                  <AIVSelect
                    v-model="authorization.auth_type"
                    :options="authTypeOptions"
                  />
                </div>
                <div
                  v-if="!isUnitBasedAuth"
                  class="col-12 col-md-10 px-2 d-flex align-items-end"
                  style="padding-bottom: 3px"
                >
                  <el-button
                    size="small"
                    @click="triggerFileSelection"
                    type="primary"
                  >
                    <i class="fa fa-file-pdf mr-1"></i>
                    {{
                      authorization.pdf_filename ? "Replace PDF" : "Upload PDF"
                    }}
                  </el-button>
                  <small
                    v-if="authorization.original_filename"
                    class="text-muted ml-3"
                  >
                    Original PDF: {{ authorization.original_filename }}
                  </small>
                </div>
                <div
                  v-if="isDSPDAuth"
                  class="col-12 col-md-10 px-2 d-flex align-items-end"
                  style="padding-bottom: 3px"
                >
                  <el-button
                    size="small"
                    @click="openBulkImportModal"
                    type="success"
                  >
                    <i class="fa fa-file-upload mr-1"></i>
                    Bulk Import from File
                  </el-button>
                </div>
              </div>

              <el-form
                ref="pdfForm"
                :model="authorization"
                label-position="top"
                label-width="120px"
              >
                <!-- DSPD/PP layout: Client Name, DSPD PID, Auth Number in one row -->
                <div v-if="isUnitBasedAuth" class="row mb-3 w-100 mx-0">
                  <div class="col-md-4 px-2">
                    <div class="field-header">
                      <label class="field-label">Client Name</label>
                      <el-checkbox
                        v-model="isNewClient"
                        @change="onNewClientToggle"
                        class="new-toggle"
                      >
                        New
                      </el-checkbox>
                    </div>
                    <el-input
                      v-if="isNewClient"
                      v-model="authorization.client_name"
                      placeholder="Enter new client name..."
                    />
                    <AIVSelect
                      v-else
                      v-model="selectedClientId"
                      :options="filteredClientOptions"
                      placeholder="Type to search clients..."
                      @change="onClientSelected"
                    />
                  </div>
                  <div class="col-md-4 px-2">
                    <label class="d-block" style="margin-bottom: 2px !important">{{ isPPAuth ? 'PP Client ID' : 'DSPD PID' }}</label>
                    <el-input v-model="authorization.dspd_pid" />
                  </div>
                  <div class="col-md-4 px-2">
                    <label class="d-block" style="margin-bottom: 2px !important">Authorization Number</label>
                    <el-tooltip
                      :content="authorizationBeingEdited
                        ? 'Authorization Numbers cannot be modified.'
                        : 'Authorization Numbers are automatically generated.'"
                      placement="top"
                    >
                      <el-input
                        v-model="authorization.auth_number"
                        disabled
                        class="disabled-dark"
                      />
                    </el-tooltip>
                  </div>
                </div>
                <!-- VR layout: Client Name & Counselor Name -->
                <div v-else class="row mb-3 w-100 mx-0">
                  <div class="col-md-6 px-2">
                    <div class="field-header">
                      <label class="field-label">Client Name</label>
                      <el-checkbox
                        v-model="isNewClient"
                        @change="onNewClientToggle"
                        class="new-toggle"
                      >
                        New
                      </el-checkbox>
                    </div>
                    <el-input
                      v-if="isNewClient"
                      v-model="authorization.client_name"
                      placeholder="Enter new client name..."
                    />
                    <AIVSelect
                      v-else
                      v-model="selectedClientId"
                      :options="filteredClientOptions"
                      placeholder="Type to search clients..."
                      @change="onClientSelected"
                    />
                  </div>
                  <div class="col-md-6 px-2">
                    <label class="d-block" style="margin-bottom: 2px !important">Counselor Name</label>
                    <AIVSelect
                      v-model="selectedCounselorId"
                      :options="counselorOptions"
                      placeholder="Type to search counselors..."
                      @change="onCounselorSelected"
                    />
                  </div>
                </div>
                <!-- VR: Client VR ID & Authorization Number -->
                <div v-if="!isUnitBasedAuth" class="row mb-3 w-100 mx-0">
                  <div class="col-md-6 px-2">
                    <label class="d-block" style="margin-bottom: 2px !important">Client VR ID</label>
                    <el-input v-model="authorization.client_vr_id" />
                  </div>
                  <div class="col-md-6 px-2">
                    <label class="d-block" style="margin-bottom: 2px !important">Authorization Number</label>
                    <el-input v-model="authorization.auth_number" />
                  </div>
                </div>
                <AIVRow
                  :record.sync="authorization"
                  :fields="[
                    { label: 'Fiscal Year', key: 'fiscal_year' },
                    {
                      label: 'Begin Date',
                      key: 'auth_begin_date',
                      type: 'date',
                    },
                    { label: 'End Date', key: 'auth_end_date', type: 'date' },
                  ]"
                />
                <AIVRow
                  v-if="!isUnitBasedAuth"
                  :record.sync="authorization"
                  :fields="[
                    {
                      label: 'Description',
                      key: 'auth_description',
                      type: 'textarea',
                      rows: 3,
                    },
                  ]"
                />
                <AIVRow
                  :record.sync="authorization"
                  :fields="serviceCodeHoursRateFields"
                />
                <AIVRow
                  v-if="isUnitBasedAuth"
                  :record.sync="authorization"
                  :fields="dspdUnitsFields"
                />
                <AIVRow
                  v-if="!isUnitBasedAuth && !isJobDevelopmentCode"
                  :record.sync="authorization"
                  :fields="[
                    {
                      label: 'Low Hours Threshold',
                      key: 'low_hours_threshold',
                      type: 'number',
                      placeholder:
                        'Hours remaining threshold for alerts (default: 10)',
                      hint: 'Email alert will be sent when hours remaining reach this threshold',
                    },
                  ]"
                />
              </el-form>

              <!-- Action buttons under the form, left-aligned with the form
                   fields (px-2 matches AIVRow), so they sit directly beneath
                   the last field instead of below the tall PDF viewer. -->
              <div class="row w-100 mx-0 mt-2">
                <div class="col-12 px-2 text-left">
                  <el-button
                    v-if="!processing && !pdfLoading && authorizationBeingEdited && !isUnitBasedAuth && !pdfUrl"
                    type="info"
                    round
                    @click="uploadAuthorizationPdf"
                  >
                    <i class="fa fa-file-pdf mr-1"></i>
                    Upload Authorization
                  </el-button>
                  <el-button
                    v-if="!processing"
                    type="primary"
                    round
                    @click="saveAuthorization"
                  >
                    {{ authorizationBeingEdited ? "Update" : "Create" }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="pdfUrl" class="col-12 mt-3">
            <div class="pdf-viewer-container">
              <iframe :src="pdfUrl" class="pdf-viewer"> </iframe>
            </div>
          </div>
        </div>
      </div>
    </AIVDialog>

    <!-- Approved Shifts Modal -->
    <ApprovedShiftsModal
      :visible.sync="showApprovedShiftsModal"
      :authorization-data="selectedAuthorizationForShifts"
      @authorization-data-changed="handleAuthorizationDataChanged"
    />
    <!-- Confirm New Client Modal -->
    <ConfirmNewClientModal
      :visible="showConfirmNewClientModal"
      :client-name="authorization.client_name"
      :matches="confirmNewClientMatches"
      @update:visible="showConfirmNewClientModal = $event"
      @select-client="onConfirmSelectClient"
      @confirm-new="onConfirmNewClient"
      @cancel="showConfirmNewClientModal = false"
    />
    <!-- Attach Future Auth Modal -->
    <AttachFutureAuthModal
      :visible="showFutureAuthModal"
      :client-id="futureAuthClientId"
      :service-code-id="futureAuthServiceCodeId"
      :authorization-id="newAuthorizationId"
      :payer-type="futureAuthPayerType"
      @update:visible="showFutureAuthModal = $event"
      @attached="handleFutureAuthAttached"
    />

    <!-- Bulk Import Modal -->
    <AIVDialog
      v-model="showBulkImportModal"
      title="Bulk Import DSPD Authorizations"
      width="900px"
    >
      <div class="bulk-import-body" style="padding: 20px; max-height: calc(85vh - 150px); overflow-y: auto">
        <!-- File Upload Area -->
        <div class="bulk-upload-area">
          <input
            ref="bulkFileInput"
            type="file"
            accept=".csv,.xls,.xlsx"
            @change="handleBulkFileSelect"
            style="display: none"
          />
          <div
            v-if="!bulkImportFile"
            class="bulk-upload-placeholder"
            @click="$refs.bulkFileInput.click()"
          >
            <i class="fa fa-cloud-upload-alt" style="font-size: 36px; margin-bottom: 8px; display: block"></i>
            <p style="margin: 0">Click to select a .csv, .xls, or .xlsx file</p>
            <small style="opacity: 0.6">Provider Caseload Budget Report</small>
          </div>
          <div v-else class="bulk-file-selected">
            <i class="fa fa-file-alt" style="font-size: 20px; color: #67c23a; margin-right: 8px"></i>
            <span>{{ bulkImportFile.name }}</span>
            <button class="btn btn-sm btn-link text-danger ml-2" @click="clearBulkFile">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Optional Date Overrides -->
        <div class="row mt-3">
          <div class="col-6">
            <label class="d-block" style="margin-bottom: 2px">Override Begin Date (optional)</label>
            <el-date-picker
              v-model="bulkImportBeginDate"
              type="date"
              placeholder="Use dates from file"
              format="MM/dd/yyyy"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            />
          </div>
          <div class="col-6">
            <label class="d-block" style="margin-bottom: 2px">Override End Date (optional)</label>
            <el-date-picker
              v-model="bulkImportEndDate"
              type="date"
              placeholder="Use dates from file"
              format="MM/dd/yyyy"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            />
          </div>
        </div>

        <!-- Results Section -->
        <div v-if="bulkImportResults" class="bulk-import-results mt-4">
          <h6 style="margin-bottom: 12px">Import Results</h6>
          <div class="row text-center mb-3">
            <div class="col-3">
              <div class="bulk-stat bulk-stat-success">
                <div class="bulk-stat-number">{{ bulkImportResults.summary.created }}</div>
                <div class="bulk-stat-label">Created</div>
              </div>
            </div>
            <div class="col-3">
              <div class="bulk-stat bulk-stat-info">
                <div class="bulk-stat-number">{{ bulkImportResults.summary.updated || 0 }}</div>
                <div class="bulk-stat-label">Updated</div>
              </div>
            </div>
            <div class="col-3">
              <div class="bulk-stat bulk-stat-warning">
                <div class="bulk-stat-number">{{ bulkImportResults.summary.skipped }}</div>
                <div class="bulk-stat-label">Skipped</div>
              </div>
            </div>
            <div class="col-3">
              <div class="bulk-stat bulk-stat-danger">
                <div class="bulk-stat-number">{{ bulkImportResults.summary.errors }}</div>
                <div class="bulk-stat-label">Errors</div>
              </div>
            </div>
          </div>

          <!-- Created detail -->
          <div v-if="bulkImportResults.details.created.length > 0" class="mt-3">
            <h6 style="color: #67c23a">Created ({{ bulkImportResults.details.created.length }})</h6>
            <div class="bulk-detail-table-wrapper">
              <table class="bulk-detail-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>PID</th>
                    <th>Service</th>
                    <th>Auth #</th>
                    <th>Units</th>
                    <th>Used</th>
                    <th>Rate</th>
                    <th>Plan $</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in bulkImportResults.details.created" :key="'c-' + item.authId">
                    <td>{{ item.row }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.pid }}</td>
                    <td>{{ item.svcCode }}</td>
                    <td>{{ item.authNumber }}</td>
                    <td>{{ item.units }}</td>
                    <td>{{ item.unitsUsed }}</td>
                    <td>{{ item.rate ? '$' + item.rate.toFixed(2) : '-' }}</td>
                    <td>{{ item.planDollars ? '$' + item.planDollars.toLocaleString() : '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Updated detail -->
          <div v-if="(bulkImportResults.details.updated || []).length > 0" class="mt-3">
            <h6 style="color: #409eff">Updated ({{ bulkImportResults.details.updated.length }})</h6>
            <div class="bulk-detail-table-wrapper">
              <table class="bulk-detail-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>PID</th>
                    <th>Service</th>
                    <th>Auth #</th>
                    <th>Units</th>
                    <th>Used</th>
                    <th>Rate</th>
                    <th>Plan $</th>
                    <th>Matched On</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in bulkImportResults.details.updated" :key="'u-' + item.authId">
                    <td>{{ item.row }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.pid }}</td>
                    <td>{{ item.svcCode }}</td>
                    <td>{{ item.authNumber }}</td>
                    <td>{{ item.units }}</td>
                    <td>{{ item.unitsUsed }}</td>
                    <td>{{ item.rate ? '$' + item.rate.toFixed(2) : '-' }}</td>
                    <td>{{ item.planDollars ? '$' + item.planDollars.toLocaleString() : '-' }}</td>
                    <td>{{ item.reason }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Skipped detail -->
          <div v-if="bulkImportResults.details.skipped.length > 0" class="mt-3">
            <h6 style="color: #e6a23c">Skipped ({{ bulkImportResults.details.skipped.length }})</h6>
            <div class="bulk-detail-table-wrapper">
              <table class="bulk-detail-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>PID</th>
                    <th>Service</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in bulkImportResults.details.skipped" :key="'s-' + idx">
                    <td>{{ item.row }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.pid }}</td>
                    <td>{{ item.svcCode }}</td>
                    <td>{{ item.reason }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Errors detail -->
          <div v-if="bulkImportResults.details.errors.length > 0" class="mt-3">
            <h6 style="color: #f56c6c">Errors ({{ bulkImportResults.details.errors.length }})</h6>
            <div class="bulk-detail-table-wrapper">
              <table class="bulk-detail-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>PID</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in bulkImportResults.details.errors" :key="'e-' + idx">
                    <td>{{ item.row }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.pid }}</td>
                    <td>{{ item.reason }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="dialog-footer text-center">
          <el-button @click="showBulkImportModal = false" round>
            {{ bulkImportResults ? 'Close' : 'Cancel' }}
          </el-button>
          <el-button
            v-if="!bulkImportResults"
            type="primary"
            round
            :disabled="!bulkImportFile || bulkImporting"
            @click="handleBulkImport"
          >
            <span v-if="bulkImporting">
              <span class="spinner-border spinner-border-sm mr-1"></span>
              Importing...
            </span>
            <span v-else>
              <i class="fa fa-upload mr-1"></i>
              Import
            </span>
          </el-button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import { authorizationsService } from "src/serviceHandlers/authorizationsService";
import { serviceCodesService } from "src/serviceHandlers/serviceCodesService";
import { clientsService } from "src/serviceHandlers/clientsService";
import { counselorsService } from "src/serviceHandlers/counselorsService";
import { emailRepliesService } from "src/serviceHandlers/emailRepliesService";
import pdfUploadAPIservice from "src/serviceHandlers/pdfUploadService";
import router from "src/routes/routes";
import ApprovedShiftsModal from "src/components/ApprovedShiftsModal.vue";
import AttachFutureAuthModal from "src/components/AttachFutureAuthModal.vue";
import ConfirmNewClientModal from "src/components/ConfirmNewClientModal.vue";
import { PDFDocument } from "pdf-lib";

const pdfService = new pdfUploadAPIservice(router);

export default {
  name: "Authorizations",
  components: {
    ApprovedShiftsModal,
    AttachFutureAuthModal,
    ConfirmNewClientModal,
  },
  data() {
    return {
      authorizationBeingEdited: null,
      authorizationColumns: [
        { key: "auth_number", label: "Auth Number" },
        { key: "counselor_display_name", label: "Counselor" },
        { key: "client_display_name", label: "Client Name" },
        {
          key: "auth_begin_date_formatted",
          label: "Begin Date",
          class: "hide-on-medium",
        },
        {
          key: "days_since_start",
          label: "Days Since Start",
          class: "hide-on-medium",
        },
        {
          key: "auth_end_date_formatted",
          label: "End Date",
          class: "hide-on-medium",
        },
        { key: "hours_display", label: "Total" },
        { key: "hours_remaining_display", label: "Remaining" },
        { key: "rate_display", label: "Rate", class: "hide-on-medium" },
        { key: "service_code_abbr", label: "Service" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      newAuthorization: {
        auth_number: "",
        client_vr_id: "",
        dspd_pid: "",
        client_name: "",
        auth_begin_date: "",
        auth_end_date: "",
        auth_description: "",
        counselor_name: "",
        service_code_id: null,
        fiscal_year: "",
        hours: null,
        units: null,
        remaining_units: null,
        rate: null,
        low_hours_threshold: 10,
        low_units_threshold: null,
        pdf_filename: null,
        original_filename: null,
        auth_type: "VR",
      },
      searchQuery: "",
      activeStatusFilter: "active",
      activeStatusOptions: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      payerTypeFilter: null,
      payerTypeOptions: [
        { id: "VR", name: "VR" },
        { id: "DSPD", name: "DSPD" },
        { id: "PP", name: "Private Pay" },
      ],
      authTypeOptions: [
        { id: "VR", name: "VR" },
        { id: "DSPD", name: "DSPD" },
        { id: "PP", name: "Private Pay" },
      ],
      sortProp: "hours_remaining",
      sortOrder: "asc",
      authorizations: [],
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0,
      },
      serviceCodes: [],

      pdfFile: null,
      pdfUrl: null,
      pdfProcessed: false,
      pdfLoading: false,
      processing: false,
      originalData: {},

      clientOptions: [],
      selectedClientId: null,
      selectedClient: null,
      isNewClient: false,
      counselorOptions: [],
      selectedCounselorId: null,
      showConfirmNewClientModal: false,
      confirmNewClientMatches: [],
      pendingSaveAfterConfirm: false,

      showApprovedShiftsModal: false,
      selectedAuthorizationForShifts: null,
      showFutureAuthModal: false,
      newAuthorizationId: null,
      futureAuthClientId: null,
      futureAuthServiceCodeId: null,
      futureAuthPayerType: 'VR',

      // Bulk import
      showBulkImportModal: false,
      bulkImportFile: null,
      bulkImportBeginDate: null,
      bulkImportEndDate: null,
      bulkImporting: false,
      bulkImportResults: null,
    };
  },
  computed: {
    authorization: {
      get() {
        return this.authorizationBeingEdited || this.newAuthorization;
      },
      set(val) {
        if (this.authorizationBeingEdited) {
          this.authorizationBeingEdited = val;
        } else {
          this.newAuthorization = val;
        }
      },
    },
    isDSPDAuth() {
      return this.authorization.auth_type === "DSPD";
    },
    isPPAuth() {
      return this.authorization.auth_type === "PP";
    },
    isUnitBasedAuth() {
      return this.isDSPDAuth || this.isPPAuth;
    },
    filteredClientOptions() {
      if (this.isPPAuth) {
        return this.clientOptions.filter((c) => c.payer_type === "Private");
      }
      const type = this.isDSPDAuth ? "DSPD" : "VR";
      return this.clientOptions.filter((c) => c.payer_type === type);
    },
    isJobDevelopmentCode() {
      const JD_CODES = ['TWE', 'SJBT', 'SE', 'CE'];
      if (!this.authorization.service_code_id) return false;
      const sc = this.serviceCodes.find(s => s.id === this.authorization.service_code_id);
      return sc ? JD_CODES.includes(sc.code) : false;
    },
    authorizationModalTitle() {
      if (this.authorizationBeingEdited) {
        const authType = this.isPPAuth ? "Private Pay" : (this.isDSPDAuth ? "DSPD" : "VR");
        return `Edit ${authType} Authorization`;
      }
      return "Create Authorization";
    },
    clientIdFields() {
      const authNumberField = {
        label: "Authorization Number",
        key: "auth_number",
      };

      // Only for DSPD/PP: disable and add tooltip
      if (this.isUnitBasedAuth) {
        authNumberField.disabled = true;
        authNumberField.tooltip = this.authorizationBeingEdited
          ? "Authorization Numbers cannot be modified."
          : "Authorization Numbers are automatically generated.";
      }

      return [
        { label: "Client Name", key: "client_name" },
        {
          label: this.isPPAuth ? "PP Client ID" : (this.isDSPDAuth ? "DSPD PID" : "Client VR ID"),
          key: this.isUnitBasedAuth ? "dspd_pid" : "client_vr_id",
        },
        authNumberField,
      ];
    },
    serviceCodeHoursRateFields() {
      const fields = [
        {
          label: "Service Code",
          key: "service_code_id",
          type: "select",
          options: this.serviceCodes,
          placeholder: "Select service code",
        },
      ];

      if (this.isUnitBasedAuth) {
        fields.push({ label: "Rate ($)", key: "rate", type: "currency" });
      } else {
        // For VR: Service Code, Hours, Rate
        fields.push({ label: "Hours", key: "hours", type: "number" });
        fields.push({ label: "Rate ($)", key: "rate", type: "currency" });
      }

      return fields;
    },
    dspdUnitsFields() {
      return [
        { label: "Units", key: "units", type: "number" },
        { label: "Remaining Units", key: "remaining_units", type: "number" },
        {
          label: "Low Units Threshold",
          key: "low_units_threshold",
          type: "number",
          placeholder: "Units remaining threshold for alerts",
        },
      ];
    },
  },
  watch: {
    "authorization.auth_type": function (newVal, oldVal) {
      // Only react to changes when creating AND modal is open (not when resetting or editing)
      if (
        newVal !== oldVal &&
        oldVal !== undefined &&
        !this.authorizationBeingEdited &&
        this.$modal?.state?.create_or_edit_authorization
      ) {
        // Reload service codes when auth type changes during creation
        this.getServiceCodes(newVal);
        // Clear service code selection when switching auth types
        this.$set(this.authorization, "service_code_id", null);
        // Clear client selection since the filtered list changes
        this.selectedClientId = null;
        this.selectedClient = null;
        this.$set(this.authorization, "client_name", "");
        this.$set(this.authorization, "client_vr_id", "");
        this.$set(this.authorization, "dspd_pid", "");
      } else {
      }
    },
    "authorization.service_code_id": function (newVal) {
      if (newVal) {
        const selectedServiceCode = this.serviceCodes.find(
          (sc) => sc.id === newVal,
        );
        if (selectedServiceCode) {
          // Auto-populate rate when service code is selected
          if (selectedServiceCode.rate) {
            this.$set(
              this.authorization,
              "rate",
              parseFloat(selectedServiceCode.rate).toFixed(2),
            );
          }
          // Set low hours threshold based on service code (only for new authorizations and non-unit-based)
          if (!this.authorizationBeingEdited && !this.isUnitBasedAuth) {
            const code = selectedServiceCode.code;
            let threshold = 10; // default

            if (
              code === "WSA1" ||
              code === "WSA2" ||
              code === "SE" ||
              code === "CE" ||
              code === "SJBT" ||
              code === "TWE"
            ) {
              threshold = 0;
            } else if (code === "LS") {
              threshold = 6;
            } else if (code === "SB") {
              threshold = 2;
            }

            this.$set(this.authorization, "low_hours_threshold", threshold);
          }
        }
      }
    },
  },
  async mounted() {
    await Promise.all([this.fetchAuthorizations(), this.getServiceCodes(), this.fetchClientOptions(), this.fetchCounselorOptions()]);
    if (this.$route.query.uploadAttachment === "true") {
      this.processEmailAttachment();
    }
  },
  methods: {
    formatDateToMDY(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
    },

    formatHours(hours) {
      if (!hours && hours !== 0) return "";
      const numHours = parseFloat(hours);
      if (Number.isInteger(numHours)) {
        return numHours.toString();
      }
      return numHours.toString();
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return "";
      const numAmount = parseFloat(amount);
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numAmount);
    },

    calculateDaysSinceStart(startDate) {
      if (!startDate) return "";
      const start = new Date(startDate);
      const today = new Date();
      if (isNaN(start.getTime())) return "";

      const timeDiff = today.getTime() - start.getTime();
      const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

      return daysDiff >= 0 ? daysDiff : 0;
    },

    async fetchAuthorizations(pageNumber = 1) {
      this.$spinner.show();
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);
        params.append("activeStatus", this.activeStatusFilter);

        if (this.payerTypeFilter) {
          params.append("payerType", this.payerTypeFilter);
        }

        if (this.searchQuery) {
          params.append("searchTerm", this.searchQuery);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        const response = await authorizationsService.listPaginated(params);
        this.authorizations = response.rows.map((auth) => {
          // Determine which fields to use based on auth number prefix
          const authNumber = auth.auth_number || "";
          let total = 0;
          let used = 0;
          let label = "";

          if (authNumber.startsWith("D")) {
            total = parseFloat(auth.units) || 0;
            used = parseFloat(auth.units_used) || 0;
            label = " units";
          } else {
            // Default to hours for VR auths (V prefix) and future auths
            total = parseFloat(auth.hours) || 0;
            used = parseFloat(auth.hours_used) || 0;
            label = " hours";
          }

          const remaining = total - used;

          return {
            ...auth,
            client_display_name: auth.client
              ? `${auth.client.first_name} ${auth.client.last_name}`
              : "",
            client_vr_id_display: auth.client ? auth.client.vr_client_id : "",
            counselor_display_name: auth.counselor
              ? auth.counselor.name
              : "N/A",
            service_code_display: auth.service_code
              ? auth.service_code.name
              : "",
            auth_begin_date_formatted: this.formatDateToMDY(
              auth.auth_begin_date,
            ),
            days_since_start: this.calculateDaysSinceStart(
              auth.auth_begin_date,
            ),
            auth_end_date_formatted: this.formatDateToMDY(auth.auth_end_date),
            hours_display: this.formatHours(total) + label,
            hours_remaining_display: this.formatHours(remaining) + label,
            rate_display: this.formatCurrency(auth.rate),
            service_code_abbr: auth.service_code ? auth.service_code.code : "",
          };
        });
        this.pagination = response.pagination;
      } catch (error) {
        this.$notifyError("Could not load authorizations: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    onSearch() {
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },
    clearSearch() {
      this.searchQuery = "";
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },
    onStatusFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },
    onPayerTypeFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.fetchAuthorizations(1);
    },
    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.fetchAuthorizations(1);
    },

    getServiceCodes(authType = null) {
      // Determine filter type: use provided authType, or fall back to modal state, or use page filter
      let filterType = this.payerTypeFilter;

      if (authType) {
        // If authType is explicitly provided, use it
        filterType = authType;
      } else if (
        this.authorizationBeingEdited ||
        this.newAuthorization.auth_type !== "VR" ||
        this.$modal?.state?.create_or_edit_authorization
      ) {
        // Otherwise, check if we're in modal context
        filterType = this.authorization.auth_type || "VR";
      }

      // Load service codes based on filter type
      let serviceCodePromise;
      if (filterType === "VR") {
        serviceCodePromise = serviceCodesService.listVR();
      } else if (filterType === "DSPD") {
        serviceCodePromise = serviceCodesService.listDSPD();
      } else if (filterType === "PP") {
        serviceCodePromise = serviceCodesService.listPP();
      } else {
        // 'all' - load all service codes
        serviceCodePromise = serviceCodesService.list();
      }

      return serviceCodePromise
        .then((serviceCodes) => {
          this.serviceCodes = serviceCodes.map((sc) => ({
            id: sc.id,
            name: sc.code,
            code: sc.code,
            fullName: sc.name,
            fullLabel: `${sc.code} - ${sc.name}`,
            rate: sc.rate,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load service codes: " + error);
        });
    },

    async createAuthorization() {
      this.resetModal();
      await this.getServiceCodes("VR"); // Default to VR auth type
      this.$modal.open("create_or_edit_authorization");
    },

    async processEmailAttachment() {
      const { notificationId, replyId, attachmentId, filename } = this.$route.query;

      // Clean up query params from URL
      this.$router.replace({ query: {} });

      if (!notificationId || !replyId || !attachmentId) {
        this.$notifyError("Missing attachment information.");
        return;
      }

      try {
        this.resetModal();
        await this.getServiceCodes("VR");
        this.$modal.open("create_or_edit_authorization");
        this.processing = true;

        const blob = await emailRepliesService.downloadAttachment(
          notificationId,
          replyId,
          attachmentId,
        );

        const file = new File([blob], filename || "authorization.pdf", {
          type: "application/pdf",
        });

        await this.processPdfFile(file);
      } catch (error) {
        this.processing = false;
        this.$notifyError("Failed to process email attachment: " + (error.message || error));
      }
    },

    async editAuthorization(authorization) {
      this.resetModal();

      // Use the client's payer_type as the auth_type (map 'Private' to 'PP')
      const clientPayer = authorization.client?.payer_type || "VR";
      const authType = clientPayer === "Private" ? "PP" : clientPayer;

      // Load service codes for this auth type FIRST, before setting the authorization
      await this.getServiceCodes(authType);

      // Pre-select client and counselor BEFORE setting authorizationBeingEdited,
      // so that when filteredClientOptions recomputes, selectedClientId is already set
      if (authorization.client) {
        this.selectedClientId = authorization.client.id;
        this.selectedClient = this.clientOptions.find((c) => c.id === authorization.client.id) || null;
      }
      if (authorization.counselor) {
        const match = this.counselorOptions.find((c) => c.id === authorization.counselor.id);
        if (match) {
          this.selectedCounselorId = match.id;
        }
      }

      this.authorizationBeingEdited = {
        ...authorization,
        client_name: authorization.client
          ? `${authorization.client.first_name} ${authorization.client.last_name}`
          : "",
        client_vr_id: authorization.client
          ? authorization.client.vr_client_id
          : "",
        dspd_pid: authorization.client ? authorization.client.dspd_pid : "",
        counselor_name: authorization.counselor
          ? authorization.counselor.name
          : "",
        auth_begin_date: this.formatDateForInput(authorization.auth_begin_date),
        auth_end_date: this.formatDateForInput(authorization.auth_end_date),
        service_code_id: authorization.id_service_codes,
        hours: authorization.hours,
        units: authorization.units,
        remaining_units: authorization.units != null && authorization.units_used != null
          ? parseFloat(authorization.units) - parseFloat(authorization.units_used)
          : null,
        low_hours_threshold: authorization.low_hours_threshold || 10,
        low_units_threshold: authorization.low_units_threshold,
        pdf_filename: authorization.pdf_filename,
        original_filename: authorization.original_filename,
        auth_type: authType,
      };

      if (authorization.pdf_filename) {
        this.pdfProcessed = true;
        this.loadSecurePDF(authorization.pdf_filename);
      }

      this.$modal.open("create_or_edit_authorization");
    },

    uploadAuthorizationPdf() {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";

      fileInput.onchange = async (event) => {
        const file = event.target.files[0];
        if (file && file.type === "application/pdf") {
          this.$modal.open("create_or_edit_authorization");
          this.processing = true;
          try {
            const processedFile = await this.checkAndProcessMultiPagePdf(file);
            const data = await pdfService.uploadPdf(processedFile);
            this.$set(this.authorization, "pdf_filename", data.filename);
            this.$set(
              this.authorization,
              "original_filename",
              data.originalFilename,
            );
            this.pdfUrl = URL.createObjectURL(processedFile);
            this.pdfProcessed = true;
            this.$notifySuccess(
              "PDF uploaded. Click Update to save.",
            );
          } catch (error) {
            this.$notifyError("Error uploading PDF. Please try again.");
          } finally {
            this.processing = false;
          }
        } else {
          this.$modal.open("create_or_edit_authorization");
        }
      };

      fileInput.oncancel = () => {
        this.$modal.open("create_or_edit_authorization");
      };

      let dialogHandled = false;

      const handleDialogDismissal = () => {
        if (!dialogHandled) {
          dialogHandled = true;
          setTimeout(() => {
            if (
              !this.$modal.state.create_or_edit_authorization &&
              !fileInput.value
            ) {
              this.$modal.open("create_or_edit_authorization");
            }
          }, 100);
        }
      };

      window.addEventListener("focus", handleDialogDismissal, { once: true });

      setTimeout(() => {
        window.removeEventListener("focus", handleDialogDismissal);
      }, 5000);

      fileInput.click();
    },

    triggerFileSelection() {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";

      fileInput.onchange = (event) => {
        const file = event.target.files[0];
        if (file && file.type === "application/pdf") {
          this.$modal.open("create_or_edit_authorization");
          this.processPdfFile(file);
        } else {
          this.$modal.open("create_or_edit_authorization");
        }
      };

      fileInput.oncancel = () => {
        this.$modal.open("create_or_edit_authorization");
      };

      let dialogHandled = false;

      const handleDialogDismissal = () => {
        if (!dialogHandled) {
          dialogHandled = true;
          setTimeout(() => {
            if (
              !this.$modal.state.create_or_edit_authorization &&
              !fileInput.value
            ) {
              this.$modal.open("create_or_edit_authorization");
            }
          }, 100);
        }
      };

      window.addEventListener("focus", handleDialogDismissal, { once: true });

      setTimeout(() => {
        window.removeEventListener("focus", handleDialogDismissal);
      }, 5000);

      fileInput.click();
    },

    openBulkImportModal() {
      this.bulkImportFile = null;
      this.bulkImportBeginDate = null;
      this.bulkImportEndDate = null;
      this.bulkImporting = false;
      this.bulkImportResults = null;
      this.showBulkImportModal = true;
    },

    handleBulkFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.bulkImportFile = file;
      this.bulkImportResults = null;
    },

    clearBulkFile() {
      this.bulkImportFile = null;
      this.bulkImportResults = null;
      if (this.$refs.bulkFileInput) {
        this.$refs.bulkFileInput.value = "";
      }
    },

    async handleBulkImport() {
      if (!this.bulkImportFile) return;

      this.bulkImporting = true;
      try {
        const response = await authorizationsService.bulkImport(
          this.bulkImportFile,
          this.bulkImportBeginDate,
          this.bulkImportEndDate,
        );

        this.bulkImportResults = response;

        const created = response.summary?.created || 0;
        const updated = response.summary?.updated || 0;
        if (created > 0 || updated > 0) {
          this.$notifySuccess(
            `Import complete: ${created} authorization(s) created, ${updated} updated`,
          );
          this.fetchAuthorizations();
        } else if (response.summary && response.summary.skipped > 0) {
          this.$notifyInfo("All rows were skipped (active authorizations with different start dates)");
        }
      } catch (error) {
        const msg = error.response?.data?.message || error.message || "Failed to import file";
        this.$notifyError(msg);
      } finally {
        this.bulkImporting = false;
      }
    },

    async fetchClientOptions() {
      try {
        const response = await clientsService.list({ activeStatus: "active", slim: true });
        this.clientOptions = (response || []).map((client) => ({
          id: client.id,
          name: `${client.first_name} ${client.last_name}`,
          vr_client_id: client.vr_client_id,
          dspd_pid: client.dspd_pid,
          payer_type: client.payer_type,
        }));
      } catch {
        this.clientOptions = [];
      }
    },

    onClientSelected(clientId) {
      const client = this.clientOptions.find((c) => c.id === clientId);
      if (client) {
        this.selectedClient = client;
        this.$set(this.authorization, "client_name", client.name);
        if (this.isUnitBasedAuth) {
          this.$set(this.authorization, "dspd_pid", client.dspd_pid || "");
        } else {
          this.$set(this.authorization, "client_vr_id", client.vr_client_id || "");
        }
      } else {
        this.selectedClient = null;
        this.$set(this.authorization, "client_name", "");
        this.$set(this.authorization, "client_vr_id", "");
        this.$set(this.authorization, "dspd_pid", "");
      }
    },

    onNewClientToggle(isNew) {
      if (isNew) {
        // Switching to new client mode - clear selection
        this.selectedClientId = null;
        this.selectedClient = null;
        this.$set(this.authorization, "client_name", "");
        this.$set(this.authorization, "client_vr_id", "");
        this.$set(this.authorization, "dspd_pid", "");
      } else {
        // Switching back to select mode - clear typed name
        this.$set(this.authorization, "client_name", "");
        this.$set(this.authorization, "client_vr_id", "");
        this.$set(this.authorization, "dspd_pid", "");
      }
    },

    async fetchCounselorOptions() {
      try {
        const response = await counselorsService.list();
        this.counselorOptions = (response || []).map((c) => ({
          id: c.id,
          name: c.name,
        }));
      } catch {
        this.counselorOptions = [];
      }
    },

    onCounselorSelected(counselorId) {
      const counselor = this.counselorOptions.find((c) => c.id === counselorId);
      if (counselor) {
        this.$set(this.authorization, "counselor_name", counselor.name);
      } else {
        this.$set(this.authorization, "counselor_name", "");
      }
    },

    onConfirmSelectClient(client) {
      // Select the confirmed client from the match modal
      const option = this.clientOptions.find((c) => c.id === client.id);
      if (option) {
        this.selectedClientId = option.id;
        this.onClientSelected(option.id);
        this.isNewClient = false;
      }
      this.showConfirmNewClientModal = false;
      if (this.pendingSaveAfterConfirm) {
        this.pendingSaveAfterConfirm = false;
        this.executeSave();
      }
    },

    matchPdfClientName(clientName) {
      // Try to find a matching VR client by name
      const normalizedName = clientName.toLowerCase().trim();
      const match = this.clientOptions.find((c) => {
        // Check for VR clients only (those with a vr_client_id)
        if (!c.vr_client_id) return false;
        return c.name.toLowerCase().trim() === normalizedName;
      });

      if (match) {
        // Found a match - select this client
        this.isNewClient = false;
        this.selectedClientId = match.id;
        this.selectedClient = match;
        this.$set(this.authorization, "client_name", match.name);
        this.$set(this.authorization, "client_vr_id", match.vr_client_id || "");
      } else {
        // No match - enable new client mode with the PDF name
        this.isNewClient = true;
        this.selectedClientId = null;
        this.selectedClient = null;
      }
    },

    matchPdfCounselorName(counselorName) {
      const normalizedName = counselorName.toLowerCase().trim();
      const match = this.counselorOptions.find(
        (c) => c.name.toLowerCase().trim() === normalizedName,
      );

      if (match) {
        this.selectedCounselorId = match.id;
        this.$set(this.authorization, "counselor_name", match.name);
      }
    },

    onConfirmNewClient() {
      this.showConfirmNewClientModal = false;
      if (this.pendingSaveAfterConfirm) {
        this.pendingSaveAfterConfirm = false;
        this.executeSave();
      }
    },

    resetModal() {
      this.authorizationBeingEdited = null;
      this.resetPdf();
      this.selectedClient = null;
      this.selectedClientId = null;
      this.isNewClient = false;
      this.selectedCounselorId = null;
      this.showConfirmNewClientModal = false;
      this.confirmNewClientMatches = [];
      this.pendingSaveAfterConfirm = false;
      this.newAuthorization = {
        auth_number: "",
        client_vr_id: "",
        dspd_pid: "",
        client_name: "",
        auth_begin_date: "",
        auth_end_date: "",
        auth_description: "",
        counselor_name: "",
        service_code_id: null,
        fiscal_year: "",
        hours: null,
        units: null,
        remaining_units: null,
        rate: null,
        low_hours_threshold: 10,
        low_units_threshold: null,
        pdf_filename: null,
        original_filename: null,
        auth_type: "VR",
      };
    },

    async processPdfFile(file) {
      this.pdfFile = file;
      this.processing = true;

      try {
        // Check if PDF has multiple pages and extract first page if needed
        const processedFile = await this.checkAndProcessMultiPagePdf(file);

        this.pdfUrl = URL.createObjectURL(processedFile);

        const data = await pdfService.uploadPdf(processedFile);

        this.originalData = { ...data };

        let serviceCodeId = data.serviceCodeId;
        if (!serviceCodeId && data.serviceCode) {
          const matchingServiceCode = this.serviceCodes.find(
            (sc) => sc.code === data.serviceCode,
          );
          serviceCodeId = matchingServiceCode ? matchingServiceCode.id : null;
        }

        // Determine default low hours threshold based on service code
        let defaultLowHoursThreshold = 10;
        const selectedServiceCode = this.serviceCodes.find(
          (sc) => sc.id === serviceCodeId,
        );
        if (selectedServiceCode) {
          const code = selectedServiceCode.code;
          if (
            code === "WSA1" ||
            code === "WSA2" ||
            code === "SE" ||
            code === "CE" ||
            code === "SJBT" ||
            code === "TWE"
          ) {
            defaultLowHoursThreshold = 0;
          } else if (code === "LS") {
            defaultLowHoursThreshold = 6;
          } else if (code === "SB") {
            defaultLowHoursThreshold = 2;
          }
        }

        this.newAuthorization = {
          auth_number: data.authorizationNumber || "",
          client_vr_id: data.clientID || "",
          client_name: data.clientName || "",
          auth_begin_date: this.formatDateForInput(data.authorizationBegin),
          auth_end_date: this.formatDateForInput(data.authorizationEnd),
          auth_description: data.description || "",
          counselor_name: data.counselor || "",
          service_code_id: serviceCodeId,
          fiscal_year: data.fiscalYear || "",
          hours: data.calculatedHours || null,
          rate: data.hourlyRate ? parseFloat(data.hourlyRate).toFixed(2) : null,
          low_hours_threshold: defaultLowHoursThreshold,
          pdf_filename: data.filename || null,
          original_filename: data.originalFilename || null,
          auth_type: this.newAuthorization.auth_type || "VR",
        };

        this.pdfProcessed = true;

        // Try to match the extracted client name with a VR client in the system
        if (data.clientName) {
          this.matchPdfClientName(data.clientName);
        }
        // Try to match the extracted counselor name
        if (data.counselor) {
          this.matchPdfCounselorName(data.counselor);
        }
      } catch (error) {
        this.$notifyError("Error processing PDF. Please try again.");
      } finally {
        this.processing = false;
      }
    },

    async checkAndProcessMultiPagePdf(file) {
      try {
        // Read the PDF file as array buffer
        const arrayBuffer = await file.arrayBuffer();

        // Load the PDF document
        const pdfDoc = await PDFDocument.load(arrayBuffer);

        // Get page count
        const pageCount = pdfDoc.getPageCount();

        // If more than one page, show notification and extract first page
        if (pageCount > 1) {
          this.$notifyInfo(
            `This PDF has ${pageCount} pages. Only the first page will be processed.`,
          );

          // Create a new PDF with only the first page
          const newPdfDoc = await PDFDocument.create();
          const [firstPage] = await newPdfDoc.copyPages(pdfDoc, [0]);
          newPdfDoc.addPage(firstPage);

          // Serialize the new PDF
          const pdfBytes = await newPdfDoc.save();

          // Create a new File object with the first page only
          return new File([pdfBytes], file.name, { type: "application/pdf" });
        }

        // If single page, return original file
        return file;
      } catch (error) {
        // If there's an error reading the PDF, return the original file
        return file;
      }
    },

    parseClientName(fullName) {
      if (!fullName || typeof fullName !== "string")
        return { first_name: "", last_name: "" };

      const trimmedName = fullName.trim();

      if (trimmedName.includes(",")) {
        const parts = trimmedName.split(",").map((part) => part.trim());
        if (parts.length >= 2) {
          return {
            first_name: parts[1] || "",
            last_name: parts[0] || "",
          };
        }
      }

      const nameParts = trimmedName
        .split(" ")
        .filter((part) => part.length > 0);
      if (nameParts.length >= 2) {
        return {
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(" "),
        };
      }

      return {
        first_name: "",
        last_name: trimmedName,
      };
    },

    formatDateForInput(dateString) {
      if (!dateString) return "";

      if (dateString.includes("/")) {
        const [month, day, year] = dateString.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }

      if (dateString.includes("T") || dateString.includes(" ")) {
        return dateString.split(/[T ]/)[0];
      }

      return dateString;
    },

    resetPdf() {
      this.pdfFile = null;
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.pdfProcessed = false;
      this.pdfLoading = false;
      this.processing = false;
      this.originalData = {};
    },

    async loadSecurePDF(filename) {
      this.pdfLoading = true;

      try {
        const token = localStorage.getItem("jwtToken");
        if (!token) {
          this.$notifyError("Authentication required to view PDF");
          return;
        }

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/pdf/secure/${filename}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
            },
          },
        );

        if (!response.ok) {
          if (response.status === 401) {
            this.$notifyError("Authentication required to view PDF");
            this.$router.push({ name: "Login" });
          } else if (response.status === 403) {
            this.$notifyError("You don't have permission to view this PDF");
          } else if (response.status === 404) {
            this.$notifyError("PDF file not found");
          } else {
            this.$notifyError("Failed to load PDF file");
          }
          return;
        }

        const blob = await response.blob();
        this.pdfUrl = URL.createObjectURL(blob);
      } catch (error) {
        this.$notifyError("Failed to load PDF file");
      } finally {
        this.pdfLoading = false;
      }
    },

    deactivateAuthorization(authorization) {
      return authorizationsService
        .deactivate(authorization.id)
        .then(() => {
          this.$notifySuccess("Successfully deactivated authorization!");
          this.fetchAuthorizations(this.pagination.currentPage);
        })
        .catch((error) => {
          this.$notifyError("Error deactivating authorization: " + error);
        });
    },

    activateAuthorization(authorization) {
      return authorizationsService
        .activate(authorization.id)
        .then((response) => {
          const message =
            response.message || "Successfully reactivated authorization!";
          this.$notifySuccess(message);
          this.fetchAuthorizations(this.pagination.currentPage);
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data?.message || error.message || error;
          this.$notifyError(
            "Error reactivating authorization: " + errorMessage,
          );
        });
    },

    deleteAuthorization(authorization) {
      return authorizationsService
        .hardDelete(authorization.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted authorization!");
          this.fetchAuthorizations(this.pagination.currentPage);
        })
        .catch((error) => {
          // Check if error has a response with a message
          const errorMessage =
            error.response?.data?.message || error.message || error;
          this.$notifyError("Error deleting authorization: " + errorMessage);
        });
    },

    async checkDuplicateAuthNumber(authNumber, excludeId = null) {
      try {
        if (!authNumber || !authNumber.trim()) {
          return false;
        }

        const existingAuth = this.authorizations.find((auth) => {
          const isSameAuthNumber =
            auth.auth_number &&
            auth.auth_number.toLowerCase().trim() ===
              authNumber.toLowerCase().trim();
          const isDifferentAuth = excludeId ? auth.id !== excludeId : true;
          return isSameAuthNumber && isDifferentAuth;
        });

        return !!existingAuth;
      } catch (error) {
        return false;
      }
    },

    async saveAuthorization() {
      const authObj = this.authorization;

      const requiredFields = [
        { field: "client_name", name: "Client Name" },
        { field: "auth_begin_date", name: "Begin Date" },
        { field: "auth_end_date", name: "End Date" },
        { field: "fiscal_year", name: "Fiscal Year" },
        { field: "service_code_id", name: "Service Code" },
      ];

      // Add conditional required fields based on auth type
      if (!this.isUnitBasedAuth) {
        requiredFields.push({ field: "auth_description", name: "Description" });
        requiredFields.push({
          field: "counselor_name",
          name: "Counselor Name",
        });
        requiredFields.push({ field: "rate", name: "Rate" });
        requiredFields.push({ field: "client_vr_id", name: "Client VR ID" });
        requiredFields.push({ field: "hours", name: "Hours" });
      } else {
        requiredFields.push({ field: "dspd_pid", name: this.isPPAuth ? "PP Client ID" : "DSPD PID" });
        requiredFields.push({ field: "units", name: "Units" });
        requiredFields.push({ field: "rate", name: "Rate" });
      }

      // Auth number is always required, except for new DSPD/PP authorizations (auto-generated)
      if (this.authorizationBeingEdited || !this.isUnitBasedAuth) {
        requiredFields.push({
          field: "auth_number",
          name: "Authorization Number",
        });
      }

      for (const { field, name } of requiredFields) {
        if (
          !authObj[field] ||
          (typeof authObj[field] === "string" && authObj[field].trim() === "")
        ) {
          this.$notifyError(`${name} is required!`);
          return;
        }
      }

      // Check for duplicate authorization number (skip for new DSPD/PP authorizations as it's auto-generated)
      if (this.authorizationBeingEdited || !this.isUnitBasedAuth) {
        const isDuplicate = await this.checkDuplicateAuthNumber(
          authObj.auth_number,
          this.authorizationBeingEdited?.id,
        );

        if (isDuplicate) {
          this.$notifyError(
            `Authorization number "${authObj.auth_number}" already exists in the system. Please use a different number.`,
          );
          return;
        }
      }

      // Validate hours or units based on auth type
      if (this.isUnitBasedAuth) {
        if (authObj.units <= 0) {
          this.$notifyError("Units must be greater than 0!");
          return;
        }
      } else {
        if (authObj.hours <= 0) {
          this.$notifyError("Hours must be greater than 0!");
          return;
        }
      }

      // Remove currency formatting from rate field before validation
      const cleanRate =
        typeof authObj.rate === "string"
          ? parseFloat(authObj.rate.replace(/[$,]/g, ""))
          : parseFloat(authObj.rate);

      if (isNaN(cleanRate) || cleanRate <= 0) {
        this.$notifyError("Rate must be greater than 0!");
        return;
      }

      if (
        new Date(authObj.auth_begin_date) >= new Date(authObj.auth_end_date)
      ) {
        this.$notifyError("End date must be after begin date!");
        return;
      }

      // For NEW authorizations with new client/counselor: confirm before creating
      if (!this.authorizationBeingEdited && this.isNewClient) {
        this.$confirmAction(
          () => { this.executeSave(); },
          `A new client "${authObj.client_name}" will be created. Continue?`,
        );
        return;
      }

      this.executeSave();
    },

    executeSave() {
      const authObj = this.authorization;

      // Remove currency formatting from rate field
      const cleanRate =
        typeof authObj.rate === "string"
          ? parseFloat(authObj.rate.replace(/[$,]/g, ""))
          : parseFloat(authObj.rate);

      this.$spinner.show();

      const parsedName = this.parseClientName(authObj.client_name);
      const authData = {
        ...authObj,
        client_name: `${parsedName.first_name} ${parsedName.last_name}`.trim(),
        client_first_name: parsedName.first_name,
        client_last_name: parsedName.last_name,
      };

      // Include fields based on auth type
      if (this.isUnitBasedAuth) {
        // DSPD/PP: Include units, rate, and units_used (derived from remaining_units); exclude hours
        authData.dspd_pid = authObj.dspd_pid;
        authData.units = authObj.units;
        authData.units_used = parseFloat(authObj.units || 0) - parseFloat(authObj.remaining_units || 0);
        authData.rate = cleanRate;
        authData.low_units_threshold = authObj.low_units_threshold;
        authData.hours = 0; // Set hours to 0 for unit-based auths
      } else {
        // VR: Include rate and hours, exclude units
        authData.client_vr_id = authObj.client_vr_id;
        authData.rate = cleanRate;
        authData.hours = authObj.hours;
        authData.units = null;
      }

      let apiSaveCall;

      if (this.authorizationBeingEdited) {
        apiSaveCall = authorizationsService
          .update(authData.id, authData)
          .then(() => {
            this.$notifySuccess("Successfully updated authorization.");
          });
      } else {
        apiSaveCall = authorizationsService
          .create(authData)
          .then((response) => {
            this.$notifySuccess("Successfully created authorization.");
            // Store the created authorization data for Future auth check
            const createdAuth = response; // response is already the data
            if (
              createdAuth &&
              createdAuth.id_clients &&
              createdAuth.id_service_codes
            ) {
              this.newAuthorizationId = createdAuth.id;
              this.futureAuthClientId = createdAuth.id_clients;
              this.futureAuthServiceCodeId = createdAuth.id_service_codes;
              this.futureAuthPayerType = this.isPPAuth ? 'PP' : (this.isDSPDAuth ? 'DSPD' : 'VR');
            }
            return response;
          });
      }

      apiSaveCall
        .then(() => {
          return Promise.all([this.fetchAuthorizations(), this.fetchClientOptions()]);
        })
        .then(() => {
          // Only close modal and reset on success
          this.$modal.close("create_or_edit_authorization");
          // Check for Future authorizations after modal closes (only for new authorizations)
          const wasNewAuth = !this.authorizationBeingEdited;
          const hasAuthId = this.newAuthorizationId;
          this.resetModal();
          if (wasNewAuth && hasAuthId) {
            // Small delay to ensure modal is closed before opening new one
            setTimeout(() => {
              this.checkForFutureAuthorizations();
            }, 300);
          }
        })
        .catch((error) => {
          // Handle different error types and display appropriate messages
          if (error.response?.status === 409) {
            const errorData = error.response.data;
            this.$notifyError(
              errorData.message ||
                "Authorization number already exists in the system.",
            );
          } else if (error.response?.status === 400) {
            const errorData = error.response.data;
            this.$notifyError(errorData.message || "Invalid data provided.");
          } else {
            this.$notifyError(
              "Error saving authorization: " +
                (error.response?.data?.message || error.message || error),
            );
          }
          // Modal stays open on error so user can correct the issue
        })
        .finally(() => {
          this.$spinner.hide();
        });
    },

    viewInvoices(authorization) {
      if (!authorization.id_clients) {
        this.$notifyError("Client information not available");
        return;
      }

      this.$router.push({
        name: "ClientAuthorizationDetails",
        params: {
          clientId: authorization.id_clients,
          id: authorization.id,
        },
        query: {
          authNumber: authorization.auth_number,
          clientName: authorization.client_display_name,
          // Back button on the details page returns here, not to the client
          from: "authorizations",
        },
      });
    },

    viewApprovedShifts(authorization) {
      this.selectedAuthorizationForShifts = {
        ...authorization,
        client_display_name: authorization.client
          ? `${authorization.client.first_name} ${authorization.client.last_name}`
          : authorization.client_display_name || "Unknown Client",
      };
      this.showApprovedShiftsModal = true;
    },

    async checkForFutureAuthorizations() {
      if (!this.futureAuthClientId || !this.newAuthorizationId) {
        return;
      }

      try {
        const response = await authorizationsService.getFutureAuthorizations(
          this.futureAuthClientId,
          this.futureAuthServiceCodeId,
        );

        if (response && response.length > 0) {
          // Show the Future auth modal
          this.showFutureAuthModal = true;
        }
      } catch (error) {
        // Don't show error to user as this is a non-critical feature
      }
    },

    handleFutureAuthAttached(result) {
      // Refresh the authorizations list to show updated hours
      this.fetchAuthorizations();
      // Reset the Future auth related data
      this.newAuthorizationId = null;
      this.futureAuthClientId = null;
      this.futureAuthServiceCodeId = null;
      this.futureAuthPayerType = 'VR';
    },

    handleAuthorizationDataChanged() {
      // Refresh the authorizations list while maintaining current page, search, and filters
      this.fetchAuthorizations(this.pagination.currentPage);
    },
  },

  beforeDestroy() {
    if (this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
    }
  },
};
</script>

<style scoped>
/* 95vh dialog minus header/margins (150px), body padding (40px) and
   section padding (40px) — the PDF nearly fills the modal when
   scrolled into view below the form */
.pdf-viewer-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  height: calc(95vh - 190px);
  overflow: hidden;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
}

/* No max-height/overflow here: the dialog body is the single scroll
   context — a nested form scrollbar made modals feel unscrollable,
   especially on touch devices */
.verification-form {
  width: 100%;
}

@media (max-width: 991.98px) {
  .pdf-viewer-container {
    height: 65vh;
  }
}

h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.pdf-verification-section {
  max-width: 100%;
  /* The dialog body already pads 20px — no second layer */
  padding: 0;
}

/* Match AIVRow label styling */
label.d-block {
  margin-bottom: 2px !important;
}

/* Disabled input with darker background */
:deep(.disabled-dark .el-input__inner) {
  background-color: #4a4a4a !important;
  color: #b0b0b0 !important;
  border-color: #6a6a6a !important;
  cursor: default !important;
}

/* Compact filter section */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group.search-group {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.compact-filter {
  min-width: 150px;
  width: 150px;
}

@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-group.search-group {
    max-width: unset;
  }

  .compact-filter {
    width: 100%;
    min-width: unset;
  }
}

/* Field header with "New" checkbox */
.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 22px;
  margin-bottom: 2px;
}

.field-header .field-label {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.new-toggle {
  font-size: 14px;
  margin-bottom: 0;
}

:deep(.new-toggle .el-checkbox__label) {
  font-weight: 500;
  color: #fff !important;
  font-size: 14px;
}

/* Bulk Import Modal */
.bulk-upload-placeholder {
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  color: rgba(255, 255, 255, 0.6);
}

.bulk-upload-placeholder:hover {
  border-color: rgba(255, 255, 255, 0.4);
}

.bulk-file-selected {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #e0e0e0;
}

.bulk-stat {
  padding: 10px;
  border-radius: 6px;
}

.bulk-stat-number {
  font-size: 24px;
  font-weight: 700;
}

.bulk-stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.bulk-stat-success {
  background: rgba(103, 194, 58, 0.1);
  color: #67c23a;
}

.bulk-stat-info {
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
}

.bulk-stat-warning {
  background: rgba(230, 162, 60, 0.1);
  color: #e6a23c;
}

.bulk-stat-danger {
  background: rgba(245, 108, 108, 0.1);
  color: #f56c6c;
}

.bulk-detail-table-wrapper {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.bulk-detail-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.bulk-detail-table th {
  background: rgba(255, 255, 255, 0.05);
  padding: 6px 8px;
  text-align: left;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
}

.bulk-detail-table td {
  padding: 4px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  color: #e0e0e0;
}

.bulk-detail-table td:nth-child(2) {
  white-space: normal;
  min-width: 140px;
}

.bulk-detail-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}
</style>
